import axios from 'axios';
import type {
  EventRegistrationDetails,
  EventRegistrationResult,
  PropertyRegistrationInfo,
  PublicInvitation,
  ResidentSelfRegistrationBody,
} from '@/types';

/**
 * Axios instance for public (no-auth) visitor API calls.
 *
 * Intentionally omits `withCredentials` — visitor pages must never
 * send or receive session cookies (spec: SI-01, no session cookies).
 */
const publicClient = axios.create({
  baseURL: import.meta.env['VITE_API_BASE_URL'] ?? '/api/v1',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

publicClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response) {
      const data = error.response.data as {
        message?: string;
        code?: string;
        requestId?: string;
      };
      return Promise.reject({
        status: error.response.status,
        code: data.code ?? 'UNKNOWN_ERROR',
        message: data.message ?? error.message,
        requestId: data.requestId,
      });
    }
    return Promise.reject(error);
  },
);

// ─── Public Invitations ───────────────────────────────────────────────────────

/** GET /public/invitations/:token */
export async function getPublicInvitation(token: string): Promise<PublicInvitation> {
  const r = await publicClient.get<PublicInvitation>(`/public/invitations/${token}`);
  return r.data;
}

/** POST /public/invitations/:token/confirm — idempotent */
export async function confirmInvitation(token: string): Promise<void> {
  await publicClient.post(`/public/invitations/${token}/confirm`);
}

/** POST /public/invitations/:token/decline */
export async function declineInvitation(token: string, reason?: string): Promise<void> {
  await publicClient.post(`/public/invitations/${token}/decline`, reason ? { reason } : {});
}

/** POST /public/invitations/:token/register — visitor pre-registration */
export async function completePublicRegistration(
  token: string,
  body: { idNumber?: string; plate?: string },
): Promise<{ status: string; pin: string }> {
  const r = await publicClient.post<{ status: string; pin: string }>(
    `/public/invitations/${token}/register`,
    body,
  );
  return r.data;
}

/** POST /public/invitations/:token/photo — multipart/form-data */
export async function uploadVisitorPhoto(token: string, file: File): Promise<void> {
  const form = new FormData();
  form.append('photo', file);
  await publicClient.post(`/public/invitations/${token}/photo`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

// ─── Public Event Registrations ──────────────────────────────────────────────

/** GET /public/registrations/:linkToken */
export async function getEventRegistration(linkToken: string): Promise<EventRegistrationDetails> {
  const r = await publicClient.get<EventRegistrationDetails>(`/public/registrations/${linkToken}`);
  return r.data;
}

/** POST /public/registrations/:linkToken */
export async function submitEventRegistration(
  linkToken: string,
  body: { name: string; phone: string },
): Promise<EventRegistrationResult> {
  const r = await publicClient.post<EventRegistrationResult>(
    `/public/registrations/${linkToken}`,
    body,
  );
  return r.data;
}

// ─── Resident Self-Registration (Mode B) ─────────────────────────────────────

/** GET /public/properties/:slug/registration */
export async function getPropertyRegistrationInfo(slug: string): Promise<PropertyRegistrationInfo> {
  const r = await publicClient.get<PropertyRegistrationInfo>(
    `/public/properties/${slug}/registration`,
  );
  return r.data;
}

/** POST /public/resident-registration */
export async function submitResidentSelfRegistration(
  body: ResidentSelfRegistrationBody,
): Promise<void> {
  await publicClient.post(`/public/resident-registration`, body);
}
